const DB_NAME = 'scout-offline-db'
const DB_VERSION = 2

interface OfflineVisit {
  id: string
  businessId: string
  businessName?: string
  startTime: string
  startLatitude: number
  startLongitude: number
  endTime?: string
  endLatitude?: number
  endLongitude?: number
  duration?: number
  notes?: string
  outcome?: string
  photoUrls?: string[]
  isCompleted: boolean
  synced: boolean
  serverVisitId?: string
}

interface OfflinePhoto {
  id: string
  visitId: string
  blob: Blob
  fileName: string
  synced: boolean
}

class OfflineStorage {
  private db: IDBDatabase | null = null

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        if (!db.objectStoreNames.contains('visits')) {
          const visitStore = db.createObjectStore('visits', { keyPath: 'id' })
          visitStore.createIndex('businessId', 'businessId', { unique: false })
          visitStore.createIndex('synced', 'synced', { unique: false })
        }

        if (!db.objectStoreNames.contains('photos')) {
          const photoStore = db.createObjectStore('photos', { keyPath: 'id' })
          photoStore.createIndex('visitId', 'visitId', { unique: false })
          photoStore.createIndex('synced', 'synced', { unique: false })
        }
      }
    })
  }

  private async getDb(): Promise<IDBDatabase> {
    if (!this.db) await this.init()
    return this.db!
  }

  // Visits
  async saveVisit(visit: OfflineVisit): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['visits'], 'readwrite')
      const store = tx.objectStore('visits')
      const request = store.put(visit)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  async getVisit(id: string): Promise<OfflineVisit | undefined> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['visits'], 'readonly')
      const store = tx.objectStore('visits')
      const request = store.get(id)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
    })
  }

  async getAllVisits(): Promise<OfflineVisit[]> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['visits'], 'readonly')
      const store = tx.objectStore('visits')
      const request = store.getAll()
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result || [])
    })
  }

  async getUnsyncedVisits(): Promise<OfflineVisit[]> {
    const all = await this.getAllVisits()
    return all.filter((v) => !v.synced || v.isCompleted)
  }

  async deleteVisit(id: string): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['visits'], 'readwrite')
      const store = tx.objectStore('visits')
      const request = store.delete(id)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  // Photos
  async savePhoto(photo: OfflinePhoto): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['photos'], 'readwrite')
      const store = tx.objectStore('photos')
      const request = store.put(photo)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  async getPhotosByVisit(visitId: string): Promise<OfflinePhoto[]> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['photos'], 'readonly')
      const store = tx.objectStore('photos')
      const index = store.index('visitId')
      const request = index.getAll(visitId)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result || [])
    })
  }

  async getUnsyncedPhotos(): Promise<OfflinePhoto[]> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['photos'], 'readonly')
      const store = tx.objectStore('photos')
      const request = store.getAll()
      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        const photos = request.result as OfflinePhoto[]
        resolve(photos.filter((p) => !p.synced))
      }
    })
  }

  async deletePhoto(id: string): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['photos'], 'readwrite')
      const store = tx.objectStore('photos')
      const request = store.delete(id)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }
}

export const offlineStorage = new OfflineStorage()
export type { OfflineVisit, OfflinePhoto }
